import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Mail, Clock, MapPin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRICING } from "@/data/content";
import { TEST_IDS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "hello@churchclipsstudio.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", church: "", email: "", plan: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        church: form.church.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        plan: form.plan || undefined,
      };
      const { data } = await axios.post(`${API}/contact`, payload);
      if (data && data.id) {
        setDone(true);
        toast.success("Thanks — we'll be in touch within one business day.");
        setForm({ name: "", church: "", email: "", plan: "", message: "" });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? "Please check your inputs and try again."
          : detail || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={TEST_IDS.contact.root}
      className="py-24 md:py-40 border-t border-[#E5E1DA] bg-[#1C1C1A] text-[#FDFCFB]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-24 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-2 eyebrow !text-white/60">Get in touch</div>
          <h2 className="md:col-span-8 font-serif-display font-black tracking-tight text-[clamp(38px,6vw,84px)] leading-[0.95]">
            Say hi.
            <br />
            <span className="italic text-[#E07A5F]">We&rsquo;ll do the rest.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Form */}
          <motion.form
            data-testid={TEST_IDS.contact.form}
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-7"
            noValidate
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Your name" required>
                <input
                  data-testid={TEST_IDS.contact.name}
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="dark-input"
                  placeholder="Pastor Alex"
                />
              </Field>
              <Field label="Church" required>
                <input
                  data-testid={TEST_IDS.contact.church}
                  type="text"
                  required
                  value={form.church}
                  onChange={(e) => setForm({ ...form, church: e.target.value })}
                  className="dark-input"
                  placeholder="Cedar Hollow Fellowship"
                />
              </Field>
              <Field label="Email" required>
                <input
                  data-testid={TEST_IDS.contact.email}
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="dark-input"
                  placeholder="alex@churchdomain.com"
                />
              </Field>
              <Field label="Plan you're eyeing">
                <Select
                  value={form.plan}
                  onValueChange={(v) => setForm({ ...form, plan: v })}
                >
                  <SelectTrigger
                    data-testid={TEST_IDS.contact.plan}
                    className="h-12 rounded-xl bg-white/5 border-white/15 text-white hover:bg-white/10 focus:ring-0 focus:border-[#E07A5F]"
                  >
                    <SelectValue placeholder="Not sure yet" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1A] border-white/10 text-white">
                    {PRICING.map((p) => (
                      <SelectItem key={p.slug} value={p.slug} className="focus:bg-white/10 focus:text-white">
                        {p.name} — {p.price}/mo
                      </SelectItem>
                    ))}
                    <SelectItem value="custom" className="focus:bg-white/10 focus:text-white">
                      Custom / one-off
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Tell us about your Sundays" required>
                <textarea
                  data-testid={TEST_IDS.contact.message}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={5}
                  className="dark-input resize-none"
                  placeholder="How many services a week, how long they run, what you'd like to see clipped..."
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid={TEST_IDS.contact.submit}
                type="submit"
                disabled={submitting}
                className={`btn-pill ${
                  done
                    ? "bg-[#D4AF37] text-[#1C1C1A]"
                    : "bg-[#E07A5F] text-white hover:bg-[#FDFCFB] hover:text-[#1C1C1A]"
                } disabled:opacity-70`}
              >
                {done ? (
                  <>
                    Message received <Check size={16} />
                  </>
                ) : submitting ? (
                  <>Sending…</>
                ) : (
                  <>
                    Send message <ArrowUpRight size={16} />
                  </>
                )}
              </button>
              <span className="text-white/50 text-[13px]">
                We reply within one business day. Usually sooner.
              </span>
            </div>

            {done && (
              <p
                data-testid={TEST_IDS.contact.success}
                className="mt-6 text-[15px] text-[#D4AF37]"
              >
                Thanks — your message is with us. Keep an eye on your inbox.
              </p>
            )}
          </motion.form>

          {/* Contact info card */}
          <motion.aside
            id="book"
            data-testid={TEST_IDS.calendly}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-5 rounded-3xl bg-white/[0.04] border border-white/10 p-8 md:p-10 flex flex-col"
          >
            <div className="eyebrow !text-white/60">Prefer a direct line?</div>
            <h3 className="mt-3 font-serif-display text-3xl md:text-4xl tracking-tight leading-tight">
              Reach us the old-fashioned way.
            </h3>
            <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
              No forms, no funnels. Send us a note, and a real human on our
              team will write back — usually the same day.
            </p>

            <div className="mt-10 space-y-6">
              <ContactRow
                icon={<Mail size={16} />}
                label="Email"
                value={
                  <a
                    data-testid="contact-info-email"
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-white link-underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                }
              />
              <ContactRow
                icon={<Clock size={16} />}
                label="Hours"
                value={<span className="text-white/90">Mon &ndash; Fri &middot; 9a &ndash; 5p ET</span>}
              />
              <ContactRow
                icon={<MapPin size={16} />}
                label="Response time"
                value={<span className="text-white/90">Within one business day</span>}
              />
            </div>

            <a
              data-testid="contact-info-email-cta"
              href={`mailto:${CONTACT_EMAIL}?subject=Sermon%20clip%20inquiry`}
              className="btn-pill mt-10 bg-white text-[#1C1C1A] hover:bg-[#E07A5F] hover:text-white self-start"
            >
              Email us directly
              <ArrowUpRight size={16} />
            </a>
          </motion.aside>
        </div>
      </div>

      <style>{`
        .dark-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 12px;
          padding: 14px 16px;
          color: #FDFCFB;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          transition: border-color .3s ease, background-color .3s ease;
        }
        .dark-input::placeholder { color: rgba(255,255,255,0.35); }
        .dark-input:focus { outline: none; border-color: #E07A5F; background: rgba(255,255,255,0.08); }
        textarea.dark-input { min-height: 140px; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="eyebrow !text-white/50 mb-2">
        {label} {required && <span className="text-[#E07A5F]">*</span>}
      </div>
      {children}
    </label>
  );
}

function ContactRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-0.5 w-9 h-9 rounded-full bg-white/10 grid place-items-center text-[#E07A5F]">
        {icon}
      </div>
      <div>
        <div className="eyebrow !text-white/50">{label}</div>
        <div className="mt-1 text-[15px]">{value}</div>
      </div>
    </div>
  );
}
